// ABOUTME: Post-OIDC hand-back to the native app — mints a long-lived JWT from the fresh
// ABOUTME: Kratos session and navigates to the App Link callback with the token attached.

import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { AuthLoadingScreen } from "./AuthLoading"
import { ory } from "@/model/auth/ory"
import { LOGIN_PAGE } from "@shared/constants/navigation"

// Android App Links are verified only on the app.* hosts (assetlinks.json lives
// there, deliberately not on every front env). When this page runs on an env
// host (beta.*, alpha.*) the callback must still go through the canonical app.*
// host of the same TLD or the app never opens. The JWT is host-independent and
// the app re-reads its region from the callback host, so this is safe.
const callbackOrigin = (): string => {
  const { protocol, host } = window.location
  return `${protocol}//${host.replace(/^(beta|alpha)\./, "app.")}`
}

// The Custom Tab lands here as the Kratos flow's return_to after the user
// finishes Google/Apple (the native app bakes this path into its fresh-flow
// URL). The session lives in THIS browser's cookie jar, not in the app's
// WebView — so we tokenize it (30-day long_lived_token template) and hand the
// JWT to the app through the verified App Link /auth/callback. JS-initiated
// navigation doesn't always launch an App Link, so after a short grace period
// we surface a real anchor — a user gesture launches it reliably. If the App
// Link doesn't fire at all, /auth/callback is also a normal front route that
// logs this browser in — a graceful dead-end, not a broken page.
export const MobileReturnPage = () => {
  const { t } = useTranslation()
  const [callbackUrl, setCallbackUrl] = useState<string | null>(null)
  const [showManual, setShowManual] = useState(false)

  useEffect(() => {
    let alive = true

    const run = async () => {
      try {
        // GET /sessions/whoami?tokenize_as=long_lived_token on the shared Ory client
        // (@ory/client dist/api.js:5443 → :3909-3926; cookies ride along via withCredentials).
        const { data } = await ory.toSession({ tokenizeAs: "long_lived_token" })
        if (!data.tokenized) throw new Error("no tokenized session in whoami response")
        if (!alive) return
        // Echo the app's single-use anti-fixation nonce (baked into this page's
        // return_to) back onto the callback so the native app can prove this
        // token belongs to a flow IT started. Missing/wrong nonce → the app
        // rejects the token. See docs auth-token-hardening.
        const nonce = new URLSearchParams(window.location.search).get("auth_nonce")
        const url = `${callbackOrigin()}/auth/callback?kratos_session_token=${encodeURIComponent(data.tokenized)}${
          nonce ? `&auth_nonce=${encodeURIComponent(nonce)}` : ""
        }`
        setCallbackUrl(url)
        window.location.href = url
      } catch {
        // No usable session (flow abandoned or expired) — nothing to hand back.
        if (alive) window.location.replace(LOGIN_PAGE)
      }
    }

    void run()
    const timer = window.setTimeout(() => {
      if (alive) setShowManual(true)
    }, 1600)
    return () => {
      alive = false
      window.clearTimeout(timer)
    }
  }, [])

  if (!callbackUrl || !showManual) return <AuthLoadingScreen note={t("auth.returningToApp")} />

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 px-6 text-center">
      <p className="text-sm text-gray-500">{t("auth.returningToApp")}</p>
      <a
        href={callbackUrl}
        className="rounded-lg bg-blue-600 px-6 py-2.5 text-sm font-medium text-white hover:bg-blue-700"
      >
        {t("auth.openApp")}
      </a>
    </div>
  )
}
